<template>
    <div class="container-fluid">
        <div class="row justify-content-center">
            <div class="col-12 case-settings-col mt-5">
                <h1 class="h5 font-weight-bolder mb-1">
                    <span class="icon icon-glyphicons-basic-cogwheel align-middle icon-mr"></span>
                    <span class="align-middle">Settings</span>
                </h1>
                <br />

                <!-- Settings Navigation -->
                <div class="d-flex flex-wrap border-bottom mb-4">
                    <a
                        v-for="tab in tabs"
                        :key="tab.value"
                        class="pb-2 pb-sm-3 pt-2 font-500 text-secondary settings-nav-link"
                        :class="{ 'border-bottom border-primary text-dark': currentView === tab.value }"
                        @click="setCurrentView(tab.value)"
                    >
                        {{ tab.label }}
                    </a>
                </div>

                <!-- Author -->
                <div v-show="currentView === 'author'">
                    <p class="text-secondary mb-3">Control how this learning experience appears to authors and learners.</p>
                    <!-- Basic Attributes Form -->
                    <base-form v-model="caseObj" :fields="authorFormFields" ref="basicSettingsForm">
                        <template slot="node_auto_numbering_enabled-header">
                            <div class="col-12 mt-3 mb-4">
                                <p class="mb-2">Cover Image</p>
                                <file-upload
                                    v-model="caseObj.cover_image"
                                    :max-bytes="100000000"
                                    :direct-upload="true"
                                    file-type-label="cover image"
                                    accept=".jpg|.png"
                                    size="sm"
                                ></file-upload>
                                <input
                                    v-model="caseObj.cover_image_alt_text"
                                    type="text"
                                    class="form-control form-control-sm alt-text-input"
                                    maxlength="600"
                                    placeholder="Screen reader description"
                                />
                            </div>
                        </template>
                    </base-form>
                    <!-- Email Recipients -->
                    <div v-if="showEmailRecipients">
                        <label class="mb-2">Text Response Email Recipients</label>
                        <help-text
                            content="
                                Learner text responses will be emailed to these recipients. 
                                Text responses are only emailed once a learner has successfully completed the case.
                            "
                            class="ml-2"
                        ></help-text>
                        <tag-search
                            placeholder="Add some email recipients"
                            v-model="caseObj.text_response_recipients"
                            :allow-create="true"
                            :tag-options="[]"
                        ></tag-search>
                    </div>
                    <button
                        @click="save()"
                        :disabled="caseIsReadOnly"
                        type="button"
                        class="btn btn-sm btn-primary float-right"
                    >
                        Save
                    </button>
                </div>

                <!-- Experience -->
                <div v-show="currentView === 'experience'">
                    <p class="text-secondary mb-2">Customize the learner experience.</p>
                    <base-form v-model="caseObj" :indent-columns="6" :fields="experienceFormFields"></base-form>
                    <div v-show="caseObj.learner_resources_enabled" class="pt-3">
                        <ul class="nav nav-tabs resource-tabs">
                            <div class="nav-item" v-for="(tab, index) in caseObj.resource_tabs" :key="index">
                                <a
                                    class="nav-link px-2 medium"
                                    :class="{ active: tab.isSelected }"
                                    @click="selectResourceTab(tab, index)"
                                    v-show="!tab.isDeleted"
                                >
                                    <div class="d-flex align-items-center">
                                        <span v-show="!tab.isEditing" class="py-1 px-1 mr-1">{{ tab.label }}</span>
                                        <input
                                            ref="tabInputs"
                                            v-show="tab.isEditing"
                                            v-model="tab.label"
                                            placeholder="Tab Name"
                                            type="text"
                                            maxlength="100"
                                            class="form-control form-control-sm mr-1 w-auto"
                                            @click.stop.prevent
                                            @keyup.enter="resetResourceTabEdit()"
                                        />
                                        <span
                                            v-show="tab.isSelected && !tab.isEditing"
                                            class="icon icon-100 icon-glyphicons-basic-pencil icon-btn mr-1"
                                            @click.stop.prevent="editResourceTab(tab)"
                                        ></span>
                                        <span
                                            v-show="tab.isSelected && tab.isEditing"
                                            class="icon icon-100 icon-glyphicons-basic-check icon-btn text-primary mr-1 ml-2"
                                            @click.stop.prevent="resetResourceTabEdit()"
                                        ></span>
                                    </div>
                                    <div v-show="tab.isSelected" class="position-relative selected-tab-tools">
                                        <span
                                            @click.stop.prevent="shiftTabIndex(index, -1)"
                                            class="icon icon-100 icon-glyphicons-basic-arrow-left icon-btn mr-2"
                                        ></span>
                                        <span
                                            @click.stop.prevent="shiftTabIndex(index, 1)"
                                            class="icon icon-100 icon-glyphicons-basic-arrow-right icon-btn mr-2"
                                        ></span>
                                        <span
                                            @click.stop.prevent="openRemoveTab(tab)"
                                            class="icon icon-100 icon-glyphicons-basic-bin icon-btn ml-1"
                                        ></span>
                                    </div>
                                </a>
                            </div>
                            <li
                                class="nav-item d-flex align-items-center"
                                :class="{ 'ml-2': activeResourceTabs.length }"
                            >
                                <a class="nav-link border-0 small mt-2" @click="addResourceTab()">
                                    <span>+ Create Tab</span>
                                </a>
                            </li>
                        </ul>
                        <div class="pt-5" v-if="selectedResourceTab">
                            <rich-content-editor
                                v-model="selectedResourceTab.elements"
                                :centered="false"
                                :show-info-footers="false"
                                :allowed-video-fields="['autoplay_video', 'loop_media', 'hide_video_controls']"
                                :allowed-audio-fields="['accessibility_alt_text', 'autoplay_video', 'loop_media']"
                                ref="richContentEditor"
                            ></rich-content-editor>
                        </div>
                    </div>
                    <br />
                    <br />
                    <button
                        @click="save()"
                        :disabled="caseIsReadOnly"
                        type="button"
                        class="btn btn-sm btn-primary float-right"
                    >
                        Save
                    </button>
                </div>

                <!-- Score -->
                <div v-show="currentView === 'score'">
                    <p class="text-secondary mb-3">Configure scoring.</p>
                    <base-form v-model="caseObj" :fields="scoreFormFieldsPart1" class="pt-3"></base-form>
                    <div class="mb-3 d-flex justify-content-end align-items-center">
                        <button
                            class="btn btn-link btn-sm"
                            :class="{ disabled: maxScoreLoading }"
                            @click="calculateMaxScore()"
                        >
                            <span class="icon icon-glyphicons-basic-reload-alt icon-mr"></span>
                            <span>Auto-Calculate Max Score</span>
                        </button>
                        <div
                            v-show="maxScoreLoading"
                            class="spinner-border spinner-border-sm text-primary mx-1"
                            role="status"
                        >
                            <span class="sr-only">Loading...</span>
                        </div>
                        <help-text
                            content="This calculation assumes that the learner explores all branches in the learning experience."
                        ></help-text>
                    </div>
                    <base-form v-model="caseObj" :fields="scoreFormFieldsPart2" ref="scoreSettingsForm"></base-form>
                    <br />
                    <button
                        @click="save()"
                        :disabled="caseIsReadOnly"
                        type="button"
                        class="btn btn-sm btn-primary float-right"
                    >
                        Save
                    </button>
                </div>

                <!-- Styles -->
                <div v-show="currentView === 'styles'">
                    <p class="text-secondary mb-4 pb-2">Customize the appearance of your learning experience.</p>
                    <global-style-form v-model="caseObj"></global-style-form>
                    <br />
                    <p class="mb-0 small font-weight-bold form-divider-label text-secondary">Font Size</p>
                    <hr class="mt-1 pt-1 mb-1" />
                    <p class="mb-4 small text-secondary">Adjust the base font size for this learning experience.</p>
                    <div class="d-flex align-items-center">
                        <div class="flex-grow-1" style="max-width: 200px">
                            <input
                                min="16"
                                max="18"
                                step="1"
                                type="range"
                                class="custom-range"
                                v-model="caseObj.body_font_size"
                            />
                        </div>
                        <div class="flex-grow-0 mx-3 medium mb-1">
                            <span class="mr-1">{{ fontSizeDisplayNames[caseObj.body_font_size] }}</span>
                            <span class="text-secondary" v-show="caseObj.body_font_size == 16">(default)</span>
                        </div>
                    </div>
                    <br />
                    <p class="mb-0 small font-weight-bold form-divider-label text-secondary">Color Palette</p>
                    <hr class="mt-1 pt-1 mb-1" />
                    <p class="mb-3 small text-secondary">
                        Create a reusable color palette to style text in this learning experience.
                    </p>
                    <div>
                        <div v-for="(color, index) in caseObj.theme_hex_codes" :key="color + index" class="my-2">
                            <div class="d-flex align-items-center">
                                <div class="flex-grow-0">
                                    <input
                                        type="color"
                                        v-model="caseObj.theme_hex_codes[index]"
                                        class="form-control btn color-input bg-light border-0 rounded mr-2"
                                    />
                                </div>
                                <div class="flex-grow-0 mr-2">
                                    <input
                                        v-model="caseObj.theme_hex_codes[index]"
                                        type="text"
                                        class="form-control color-hex-input"
                                    />
                                </div>
                                <div class="flex-grow-0">
                                    <span
                                        class="icon icon-glyphicons-basic-bin icon-btn"
                                        role="button"
                                        @click="removeThemeColor(index)"
                                    ></span>
                                </div>
                            </div>
                        </div>
                        <button class="btn btn-sm btn-link p-1" @click="createThemeColor()">
                            <span class="icon icon-xs icon-glyphicons-basic-plus align-middle mr-1"></span>
                            <span>Color</span>
                        </button>
                    </div>
                    <br />

                    <p class="mb-0 small font-weight-bold form-divider-label text-secondary">Heading Colors</p>
                    <hr class="mt-1 pt-1 mb-1" />
                    <p class="mb-4 small text-secondary">Set default colors for headings and hyperlinks.</p>
                    <base-form
                        v-model="caseObj"
                        :fields="colorFormFields"
                        :inline-style="true"
                        :condensed-style="true"
                        ref="colorSettingsForm"
                    ></base-form>
                    <br />
                    <button
                        @click="save()"
                        :disabled="caseIsReadOnly"
                        type="button"
                        class="btn btn-sm btn-primary float-right"
                    >
                        Save
                    </button>
                </div>

                <!-- Community -->
                <div v-show="currentView === 'community'">
                    <p class="text-secondary mb-2">
                        Community-related information, displayed only if published to the Community Library.
                    </p>
                    <base-form
                        v-model="caseObj"
                        :indent-columns="6"
                        :fields="communityFormFields"
                        ref="communitySettingsForm"
                    ></base-form>
                    <div>
                        <p class="text-secondary">
                            Add institutions that should be acknowledged as a creator of this learning experience.
                            <router-link to="/community/publishers">Manage community publishers</router-link>.
                        </p>
                        <div class="d-flex flex-wrap align-items-center mb-2">
                            <div
                                class="flex-grow-0 d-flex align-items-center mr-3 mb-2"
                                v-for="contributor in contributors"
                                :key="contributor.id"
                            >
                                <div class="flex-grow-0 mr-3">
                                    <div
                                        class="list-logo rounded-circle d-flex align-items-center justify-content-center"
                                    >
                                        <img
                                            v-if="contributor.publisher.logo_file"
                                            :src="contributor.publisher.logo_file.signed_url"
                                            class="list-logo-img"
                                            alt="Publisher logo"
                                        />
                                    </div>
                                </div>
                                <div class="flex-grow-0 mr-1">
                                    <p class="mb-0">
                                        {{ contributor.publisher.name }}
                                    </p>
                                </div>
                                <div>
                                    <span
                                        class="icon icon-glyphicons-basic-bin icon-btn p-2 align-middle"
                                        role="button"
                                        @click="openRemoveContributor(contributor)"
                                    ></span>
                                </div>
                            </div>
                        </div>
                        <button @click="openAddContributor()" class="btn btn-gray btn-sm">+ Add Contributor</button>
                    </div>
                    <br />
                    <div class="pt-3">
                        <p class="mb-0 small font-weight-bold form-divider-label text-secondary">
                            Community Categories
                        </p>
                        <hr class="mt-1 pt-1 mb-3" />
                        <p class="text-secondary">
                            Select the categories that your learning experience falls into, in order of relevance.
                        </p>
                        <v-select
                            inputId="category-select"
                            v-model="caseObj.community_tags"
                            :multiple="true"
                            :options="communityCategories"
                            :close-on-select="false"
                            label="name"
                            class="mw-100 mb-3"
                            placeholder="Select applicable categories..."
                            :reduce="(option) => option.id"
                        >
                            <template slot="option" slot-scope="option">
                                {{ option.name }}
                            </template>
                            <template v-slot:selected-option="option">
                                {{ option.name }}
                            </template>
                            <template slot="no-options">
                                <div class="py-2">
                                    <span class="text-secondary">No categories found.</span>
                                </div>
                            </template>
                        </v-select>
                    </div>
                    <br />
                    <div class="pt-3">
                        <p class="mb-0 small font-weight-bold form-divider-label text-secondary">Files</p>
                        <hr class="mt-1 pt-1 mb-3" />
                        <p class="text-secondary">
                            Upload associated files like facilitation guides and debrief documents for community usage.
                        </p>
                        <div class="d-flex flex-wrap">
                            <div
                                v-for="community_file in activeCommunityFiles"
                                :key="community_file.id"
                                class="mb-4 pr-2 pr-lg-4"
                            >
                                <div class="d-flex">
                                    <div class="flex-grow-0 mr-1">
                                        <file-upload
                                            v-model="community_file.stored_file"
                                            :direct-upload="true"
                                            :max-bytes="20000000"
                                            :warning-bytes="20000000"
                                            placeholder="Drop a new file here"
                                            file-type-label="downloadable file"
                                            accept=".docx|.pdf"
                                            :replaceable="false"
                                        ></file-upload>
                                        <input
                                            type="text"
                                            class="form-control form-control-sm"
                                            v-model="community_file.name"
                                            maxlength="150"
                                            placeholder="Name (e.g. Facilitation Guide)"
                                        />
                                    </div>
                                    <div class="flex-grow-0">
                                        <button
                                            type="button"
                                            class="btn btn-transparent py-0 px-1"
                                            @click="removeCommunityFile(community_file)"
                                        >
                                            <span class="icon icon-glyphicons-basic-bin"></span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button @click="addCommunityFile()" class="btn btn-gray btn-sm">+ Add File</button>
                    </div>
                    <br />
                    <br />
                    <button
                        @click="saveCommunity()"
                        :disabled="caseIsReadOnly"
                        type="button"
                        class="btn btn-sm btn-primary float-right"
                    >
                        Save
                    </button>
                </div>
                
                <!-- LMS Settings -->
                <div v-show="currentView === 'lms'">
                    <base-form
                        v-model="caseObj"
                        :fields="lmsFormFields"
                    ></base-form>
                    <button
                        @click="save()"
                        :disabled="caseIsReadOnly"
                        type="button"
                        class="btn btn-sm btn-primary float-right"
                    >
                        Save
                    </button>
                </div>

                <!-- Author Access -->
                <div v-show="currentView === 'access'">
                    <div class="d-flex align-items-center mb-2">
                        <div class="flex-grow-0 text-secondary">Edit team member access.</div>
                        <div class="flex-grow-0 ml-2" v-show="accessFormLoading">
                            <div class="custom-spinner custom-spinner-sm" role="status">
                                <span class="sr-only">Loading...</span>
                            </div>
                        </div>
                    </div>
                    <author-access-form
                        v-if="libraryItem"
                        v-model="libraryItem"
                        :loading.sync="accessFormLoading"
                        class="pt-3"
                        style="max-width: 530px;"
                    ></author-access-form>
                </div>

                <!-- Notes -->
                <div v-show="currentView === 'notes'">
                    <p class="text-secondary mb-4">Notes about this learning experience, for author usage.</p>
                    <rich-text v-model="caseObj.author_notes"></rich-text>
                    <br />
                    <button
                        @click="save()"
                        :disabled="caseIsReadOnly"
                        type="button"
                        class="btn btn-sm btn-primary float-right"
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>

        <!-- Contributor Modal -->
        <base-modal id="add-contributor-modal" :loading="modalLoading" :centered="true">
            <template slot="title">Add a Contributor</template>
            <template>
                <p>Tag a publisher as a contributor.</p>
                <v-select
                    v-model="selectedPublisher"
                    :multiple="false"
                    :options="publishers"
                    :close-on-select="true"
                    label="name"
                    class="mw-100 mb-3"
                    placeholder="Select a publisher..."
                >
                    <template v-slot:option="option">
                        <div class="d-flex align-items-center mw-100 case-selector-container">
                            <div class="flex-grow-0 mr-3">
                                <div class="list-logo rounded-circle d-flex align-items-center justify-content-center">
                                    <img
                                        v-if="option.logo_file"
                                        :src="option.logo_file.signed_url"
                                        class="list-logo-img"
                                        alt="Publisher logo"
                                    />
                                </div>
                            </div>
                            <div class="flex-grow-1">
                                <div class="mb-0">
                                    {{ option.name }}
                                </div>
                            </div>
                        </div>
                    </template>
                    <template v-slot:selected-option="option">
                        <div class="d-flex align-items-center mw-100 case-selector-container">
                            <div class="flex-grow-0 mr-3">
                                <div class="list-logo rounded-circle d-flex align-items-center justify-content-center">
                                    <img
                                        v-if="option.logo_file"
                                        :src="option.logo_file.signed_url"
                                        class="list-logo-img"
                                        alt="Publisher logo"
                                    />
                                </div>
                            </div>
                            <div class="flex-grow-1">
                                <div class="mb-0">
                                    {{ option.name }}
                                </div>
                            </div>
                        </div>
                    </template>
                    <template slot="no-options">
                        <div class="py-3">
                            <span class="text-secondary">You don't have access to any publishers yet.</span>
                        </div>
                    </template>
                </v-select>
                <p v-show="publishers.length == 0">
                    You don't have access to any publishers yet.
                    <router-link data-dismiss="modal" to="/community/publishers"
                        >Create a publisher profile for your organization.</router-link
                    >
                </p>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button class="btn btn-primary" type="button" :disabled="!selectedPublisher" @click="addContributor()">
                    Add
                </button>
            </template>
        </base-modal>

        <!-- Remove Contributor Modal -->
        <base-modal id="remove-contributor-modal" :loading="modalLoading" :centered="true">
            <template slot="title">Add a Contributor</template>
            <template>
                <p v-if="selectedContributor">Remove contributor {{ selectedContributor.publisher.name }}?</p>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button class="btn btn-danger" type="button" @click="removeContributor()">Remove</button>
            </template>
        </base-modal>

        <!-- Remove Contributor Modal -->
        <base-modal id="remove-tab-modal" :loading="modalLoading" :centered="true">
            <template slot="title">Remove Tab</template>
            <template>
                <p>Remove resource tab?</p>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button class="btn btn-danger" type="button" @click="removeResourceTab()">Remove</button>
            </template>
        </base-modal>
    </div>
</template>

<script>
import http from "@/axios.js"
import BaseForm from "@/components/BaseForm"
import TagSearch from "@/components/TagSearch"
import HelpText from "@/components/HelpText"
import FileUpload from "@/components/FileUpload"
import RichText from "@/components/RichText"
import BaseModal from "@/components/BaseModal"
import RichContentEditor from "@/components/RichContentEditor"
import GlobalStyleForm from "@/components/GlobalStyleForm"
import AuthorAccessForm from "@/components/AuthorAccessForm"
import { bodyColor } from "@/assets/js/settings.js"

export default {
    name: "CaseSettings",
    components: {
        BaseForm,
        TagSearch,
        HelpText,
        FileUpload,
        RichText,
        BaseModal,
        RichContentEditor,
        GlobalStyleForm,
        AuthorAccessForm,
    },
    data() {
        return {
            caseObj: {
                nodes: [],
                community_files: [],
                resource_tabs: [],
                theme_hex_codes: [],
                text_response_recipients: [],
            },
            savedCaseObj: {},
            publishers: [],
            contributors: [],
            communityCategories: [],
            currentView: "author",
            loading: false,
            maxScoreLoading: false,
            modalLoading: false,
            selectedContributor: null,
            selectedPublisher: null,
            selectedResourceTab: null,
            fontSizeDisplayNames: {
                16: "Normal",
                17: "Large",
                18: "Largest",
            },
            libraryItem: null,
            accessFormLoading: false,
        }
    },
    computed: {
        caseIsReadOnly() {
            return this.$store.getters.caseIsReadOnly
        },
        caseID() {
            return this.$route.params.id
        },
        nodeOptions() {
            return this.$store.getters.nodeOptions
        },
        lmsIsConfigured() {
            return this.$store.getters.organizationLMSAuthConfigured
        },
        tabs() {
            let tabs = [
                {
                    value: "author",
                    label: "General",
                },
                {
                    value: "access",
                    label: "Access",
                },
                {
                    value: "experience",
                    label: "Experience",
                },
                {
                    value: "styles",
                    label: "Theme",
                },
                {
                    value: "score",
                    label: "Scoring",
                },
                {
                    value: "community",
                    label: "Community",
                },
                {
                    value: "lms",
                    label: "LMS",
                },
            ]
            // Don't show the notes tab unless notes have been previously populated.
            if (this.caseObj.author_notes) {
                tabs.push({
                    value: "notes",
                    label: "Notes",
                })
            }
            // Don't show the lms tab if an LMS hasn't been configured.
            if (this.lmsIsConfigured === false) {
                let index = tabs.findIndex((tab) => tab.value === "lms")
                tabs.splice(index, 1)
            }
            return tabs
        },
        userIsAdmin() {
            return this.$store.getters.userIsOrganizationAdmin
        },
        // Hide this feature except for certain organizations, or if already set (feature to be deprecated).
        showEmailRecipients() {
            let isLegacyOrg = [1, 28].includes(this.$store.getters.currentOrganizationId)
            return isLegacyOrg
        },
        experienceFormFields() {
            return [
                {
                    type: "divider",
                    name: "Learner Interface",
                },
                {
                    name: "Display video subtitles/captions by default",
                    subtext: "When enabled, video subtitles/captions will be displayed by default.",
                    key: "display_captions_by_default",
                    type: "switch",
                    required: false,
                },
                {
                    name: "Enable screen history navigation",
                    subtext:
                        "Check this box to allow the learner to navigate to previously visited screens.",
                    key: "navigation_enabled",
                    type: "switch",
                    required: false,
                },
                {
                    name: "Select a navigation style:",
                    key: "navigation_style",
                    type: "radio",
                    options: [
                        {
                            value: "menu",
                            label: "Navigation Menu",
                        },
                        {
                            value: "back_button",
                            label: "Back Button",
                        },
                    ],
                    required: false,
                    hidden(value) {
                        return !value.navigation_enabled
                    },
                    displayInline: true,
                },
                {
                    name: "Default Language",
                    key: "ui_language",
                    required: false,
                    type: "select",
                    displayInline: true,
                    description: "The language used to label built-in buttons and elements.",
                    options: [
                        {
                            value: "en",
                            label: "English (en)",
                        },
                        {
                            value: "fr",
                            label: "French (fr)",
                        },
                    ],
                },
                {
                    type: "divider",
                    name: "Performance",
                },
                {
                    name: "Preload static files",
                    subtext: `
                        When enabled, static files on upcoming screens will be loaded ahead of time to improve performance.
                    `,
                    key: "preload_files",
                    type: "switch",
                    required: false,
                },
                {
                    name: "Preload inquiry feedback",
                    subtext: `
                        When enabled, feedback will be loaded ahead of time to reduce load times. For summative activities, turn this setting off.
                    `,
                    key: "preload_nodes",
                    type: "switch",
                    required: false,
                },
                {
                    type: "divider",
                    name: "Learner Toolbar",
                },
                {
                    name: "Show Learner Notepad",
                    subtext: `
                        When enabled, learners will be able to access their notepad throughout the learning experience.
                    `,
                    key: "global_notepad_enabled",
                    type: "switch",
                    required: false,
                },
                {
                    name: "Show Learner Resources",
                    subtext: `
                        Provide learners with resources that they can access throughout the learning experience (e.g. text, images, files).
                    `,
                    key: "learner_resources_enabled",
                    type: "switch",
                    required: false,
                },
            ]
        },
        authorFormFields() {
            return [
                {
                    name: "Title",
                    placeholder: "A title for this learning experience",
                    key: "title",
                    required: true,
                    maxlength: 150,
                },
                {
                    name: "Description",
                    key: "description",
                    placeholder: "A description of this learning experience",
                    maxlength: 500,
                    type: "textarea",
                    rows: "3",
                    required: false,
                },
                {
                    name: "Show a different title and description to learners",
                    description: "Optionally show a different title and description to learners.",
                    key: "learner_fields_enabled",
                    type: "switch",
                    required: false,
                },
                {
                    name: "Learner Title",
                    key: "learner_title",
                    subtext: "A title to display to learners (optional).",
                    maxlength: 150,
                    required: false,
                    hidden(value) {
                        return !value.learner_fields_enabled
                    },
                },
                {
                    name: "Learner Description",
                    placeholder: "One or two sentences introducing the learning experience.",
                    subtext: "A description to display to learners (optional).",
                    key: "learner_description",
                    maxlength: 2000,
                    type: "textarea",
                    rows: "3",
                    required: false,
                    hidden(value) {
                        return !value.learner_fields_enabled
                    },
                },
                {
                    name: "Automatically number screens",
                    description: `
                        When enabled, screens will be automatically numbered based on their order in the left-hand sidebar.
                    `,
                    key: "node_auto_numbering_enabled",
                    type: "switch",
                    required: false,
                },
            ]
        },
        communityFormFields() {
            let vm = this
            return [
                {
                    type: "divider",
                    name: "Basic Info",
                },
                {
                    name: "Title",
                    key: "community_title",
                    required: false,
                    maxlength: 80,
                    showLimit: true,
                },
                {
                    name: "Introduction",
                    key: "community_description",
                    required: false,
                    subtext: "One or two sentences introducing the learning experience.",
                    maxlength: vm.savedCaseObj.ui_language == "fr" ? 200 : 145,
                    type: "textarea",
                    rows: 2,
                    showLimit: true,
                },
                {
                    name: "About",
                    placeholder: "Provide a more in-depth description about the learning experience, its history, etc.",
                    key: "community_about",
                    required: false,
                    type: "textarea",
                    rows: 5,
                },
                {
                    name: "Author Credits",
                    key: "creators_credit",
                    required: false,
                    maxlength: 500,
                },
                {
                    name: "Learning Objectives",
                    key: "learning_objectives",
                    type: "textarea",
                    placeholder: "List some learning objectives here....",
                    required: false,
                    rows: 5,
                },
                {
                    name: "Target Audience",
                    placeholder: "E.g. Nursing Students",
                    key: "target_audience",
                    required: false,
                    maxlength: 500,
                    displayInline: true,
                },
                {
                    name: "Project Funding",
                    placeholder: "Project Funding Sources",
                    key: "funding",
                    required: false,
                    maxlength: 150,
                    displayInline: true,
                },
                {
                    name: "Approximate Duration",
                    key: "duration",
                    type: "number",
                    required: false,
                    displayInline: true,
                },
                {
                    name: "Duration Units",
                    key: "duration_units",
                    type: "select",
                    required: false,
                    displayInline: true,
                    options: [
                        {
                            value: "minutes",
                            label: "Minutes",
                        },
                        {
                            value: "hours",
                            label: "Hours",
                        },
                    ],
                },
                {
                    type: "divider",
                    name: "Creators",
                },
            ]
        },
        scoreFormFieldsPart1() {
            return [
                {
                    name: "Starting Score",
                    description:
                        "Optional - The initial score a learner should start with.",
                    key: "starting_score",
                    type: "number",
                    required: false,
                    displayInline: true,
                },
                {
                    name: "Maximum Achievable Score",
                    description: "Optional - The highest score a learner can achieve in the learning experience.",
                    key: "score_max",
                    type: "number",
                    required: false,
                    displayInline: true,
                    condensedStyle: true,
                },
            ]
        },
        scoreFormFieldsPart2() {
            return [
                {
                    name: "Minimum Passing Score",
                    description: "Optional - The minimum score required to be successful in the learning experience.",
                    key: "min_passing_score",
                    type: "number",
                    required: false,
                    displayInline: true,
                },
                {
                    name: "Display Live Score",
                    subtext: "When enabled, the learner's score will be displayed and updated in real time.",
                    key: "show_live_score",
                    type: "checkbox",
                    required: false,
                },
                {
                    name: "Reroute Learners Below Minimum Passing Score",
                    key: "min_score_branching_enabled",
                    subtext:
                        "Take the learner to a different screen if their score falls below the minimum passing score.",
                    type: "checkbox",
                    required: false,
                    disabled(value) {
                        return !value.show_live_score
                    },
                    condensedStyle: true,
                },
                {
                    name: "Target Screen",
                    type: "search",
                    key: "min_score_branching_node",
                    options: this.nodeOptions,
                    labelField: "label",
                    valueField: "id",
                    sortField: "number",
                    searchFields: ["label"],
                    placeholder: "Select a screen",
                    subtext: `
                        The screen to take the learner to if their score falls below the minimum.
                        Select a Failure Screen if you want to force the learner to start over.
                    `,
                    hidden(value) {
                        return !value.min_score_branching_enabled || !value.show_live_score
                    },
                    required(value) {
                        return Boolean(value.min_score_branching_enabled)
                    },
                },
            ]
        },
        colorFormFields() {
            let vm = this
            return [
                {
                    name: "Hyperlinks",
                    key: "link_color",
                    required: false,
                    description: "The default color used for hyperlinks.",
                    placeholder: "#115fde",
                    type: "color",
                    hidden() {
                        return !vm.savedCaseObj.link_color
                    },
                },
                {
                    name: "Heading 1",
                    key: "heading_1_color",
                    required: false,
                    description: "The default color used for largest heading text.",
                    placeholder: "#3a3f44",
                    type: "color",
                },
                {
                    name: "Heading 2",
                    key: "heading_2_color",
                    required: false,
                    description: "The default color used for the second largest heading text.",
                    placeholder: "#3a3f44",
                    type: "color",
                },
                {
                    name: "Heading 3",
                    key: "heading_3_color",
                    required: false,
                    description: "The default color used for the third largest heading text.",
                    placeholder: "#3a3f44",
                    type: "color",
                },
            ]
        },
        lmsFormFields() {
            return [
                {
                    name: "Show this learning experience in your LMS during content selection",
                    subtext: "Make this learning experience available to be used by any instructor in your LMS.",
                    key: "show_in_lms_deep_link",
                    type: "switch",
                    required: false,
                },
            ]
        },
        activeCommunityFiles() {
            return this.caseObj.community_files.filter((c) => c.isDeleted != true)
        },
        activeResourceTabs() {
            return this.caseObj.resource_tabs.filter((t) => t.isDeleted !== true)
        },
    },
    methods: {
        // Fetch data for the view.
        async fetchData() {
            this.$store.dispatch("addLoad")
            let vm = this
            // Fetch case details.
            await http
                .get("/api/cases/" + this.caseID + "/settings/")
                .then(function (response) {
                    vm.caseObj = response.data

                    // Flatten text response recipient, and category arrays for use in forms.
                    vm.caseObj.text_response_recipients = vm.caseObj.text_response_recipients.map((tag) => tag.email)
                    vm.caseObj.community_tags = vm.caseObj.community_tags.map((tag) => tag.category.id)

                    vm.$set(vm.caseObj, "resource_tabs", [])

                    if (!vm.caseObj.heading_1_color) {
                        vm.caseObj.heading_1_color = bodyColor
                    }
                    if (!vm.caseObj.heading_2_color) {
                        vm.caseObj.heading_2_color = bodyColor
                    }
                    if (!vm.caseObj.heading_3_color) {
                        vm.caseObj.heading_3_color = bodyColor
                    }

                    vm.savedCaseObj = Object.assign({}, vm.caseObj)
                })
                .finally(function () {
                    vm.$store.dispatch("removeLoad")
                })

            if (this.currentView == "experience") {
                this.fetchResources()
            } else if (this.currentView == "access") {
                this.fetchlibraryItem()
            }
            this.fetchContributors()
            this.fetchCommunityCategories()
        },
        fetchContributors() {
            let vm = this
            http.get(`/api/cases/${this.caseID}/contributors/`).then(function (response) {
                vm.contributors = response.data
            })
        },
        // Fetch the library item associated with the current case object being edited.
        async fetchlibraryItem() {
            // Display a loading spinner while the data for the form loads.
            this.accessFormLoading = true
            let response = await http.get(`/api/cases/${this.caseID}/library-item/`)
            this.libraryItem = response.data
        },
        fetchCommunityCategories() {
            let vm = this
            http.get("/api/community/categories/?status=all").then(function (response) {
                vm.communityCategories = response.data
            })
        },
        fetchResources() {
            let vm = this
            http.get(`/api/cases/${this.caseID}/resources/`).then(function (response) {
                vm.caseObj.resource_tabs = response.data
                let selectedTab = null
                if (vm.selectedResourceTab) {
                    selectedTab = vm.caseObj.resource_tabs.find((t) => t.id == vm.selectedResourceTab.id)
                }
                if (selectedTab) {
                    vm.selectedResourceTab = selectedTab
                    vm.$set(vm.selectedResourceTab, "isSelected", true)
                } else if (vm.caseObj.resource_tabs.length) {
                    vm.selectedResourceTab = vm.caseObj.resource_tabs[0]
                    vm.$set(vm.selectedResourceTab, "isSelected", true)
                } else {
                    vm.selectedResourceTab = null
                }
            })
        },
        // Save the case settings.
        save() {
            // Check form validity, switch to the tab with errors if any exist.
            if (!this.$refs.colorSettingsForm.validate()) {
                this.currentView = "styles"
                return
            }
            if (!this.$refs.basicSettingsForm.validate()) {
                this.currentView = "author"
                return
            }
            if (!this.$refs.scoreSettingsForm.validate()) {
                this.currentView = "score"
                return
            }
            this.$store.dispatch("addLoad")

            // Remove node data.
            delete this.caseObj.nodes

            // Hack fix - blank entry on number fields produces empty strings causing validation issues.
            if (this.caseObj.score_max === "") {
                this.caseObj.score_max = null
            }
            if (this.caseObj.starting_score === "") {
                this.caseObj.starting_score = 0
            }
            if (this.caseObj.min_passing_score === "") {
                this.caseObj.min_passing_score = null
            }

            if (!this.caseObj.min_score_branching_node) {
                this.caseObj.min_score_branching_node = null
            }

            this.caseObj.body_font_size = this.caseObj.body_font_size || null
            this.caseObj.heading_1_font_size = this.caseObj.heading_1_font_size || null
            this.caseObj.heading_2_font_size = this.caseObj.heading_2_font_size || null
            this.caseObj.heading_3_font_size = this.caseObj.heading_3_font_size || null

            let vm = this
            http.put("/api/cases/" + this.caseObj.id + "/settings/", this.caseObj).then(function () {
                vm.$store.dispatch("removeLoad")
                vm.$notify({
                    group: "main",
                    type: "success",
                    text: "Settings updated.",
                    data: {},
                })
                vm.$store.dispatch("refreshCase", vm.caseObj.id)
                vm.fetchData()
                vm.$refs.colorSettingsForm.resetValidate()
            })
        },
        async saveCommunity() {
            if (!this.$refs.communitySettingsForm.validate()) {
                return
            }
            this.save()
        },
        // Set the current view - controls which settings to display.
        setCurrentView(viewName) {
            if (viewName == this.currentView) {
                return
            } else if (viewName == "experience") {
                this.fetchResources()
            } else if (viewName == "access") {
                this.fetchlibraryItem()
            }
            this.currentView = viewName
            this.$router.push({ path: this.$route.path, params: { id: this.caseID }, query: { view: viewName } })
        },
        // Auto-calculate and set the maximum score for the case.
        calculateMaxScore() {
            this.maxScoreLoading = true
            let vm = this
            http.get(`/api/cases/${this.caseID}/max-score/`)
                .then(function (response) {
                    vm.caseObj.score_max = response.data.score
                })
                .finally(() => (vm.maxScoreLoading = false))
        },
        // Add a new theme color.
        createThemeColor() {
            this.caseObj.theme_hex_codes.push("#000000")
        },
        // Remove a theme color by its index.
        removeThemeColor(index) {
            this.caseObj.theme_hex_codes.splice(index, 1)
        },
        // Open the add-contributor modal.
        openAddContributor() {
            $("#add-contributor-modal").modal("show")
            this.selectedPublisher = null
            // Fetch the publishers that the user has access to.
            this.modalLoading = true
            let vm = this
            http.get("/api/community/publishers/")
                .then(function (response) {
                    vm.publishers = response.data
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        addContributor() {
            let vm = this
            http.post(`/api/cases/${this.caseID}/contributors/`, {
                publisherID: this.selectedPublisher.id,
            })
                .then(function () {
                    vm.fetchContributors()
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Contributor added.",
                        data: {},
                    })
                })
                .finally(function () {
                    $("#add-contributor-modal").modal("hide")
                })
        },
        openRemoveContributor(contributor) {
            this.selectedContributor = contributor
            $("#remove-contributor-modal").modal("show")
        },
        removeContributor() {
            this.modalLoading = true
            let vm = this
            http.put(`/api/cases/${this.caseID}/contributors/${this.selectedContributor.id}/remove/`)
                .then(function () {
                    vm.fetchContributors()
                    $("#remove-contributor-modal").modal("hide")
                    vm.selectedContributor = null
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Contributor removed.",
                        data: {},
                    })
                })
                .finally(function () {
                    vm.modalLoading = true
                })
        },
        addCommunityFile() {
            this.caseObj.community_files.push({
                stored_file: null,
                name: "",
            })
        },
        removeCommunityFile(communityFile) {
            this.$set(communityFile, "isDeleted", true)
        },
        // Add a new resource tab to the end of the tab list.
        addResourceTab() {
            this.resetResourceTabSelect()
            this.resetResourceTabEdit()
            this.caseObj.resource_tabs.push({
                label: "New Tab",
                isSelected: true,
                isEditing: true,
                elements: [],
            })
            let newTabIndex = this.caseObj.resource_tabs.length - 1
            this.selectedResourceTab = this.caseObj.resource_tabs[newTabIndex]
            this.$nextTick(() => this.$refs.tabInputs[newTabIndex].focus())
        },
        // Select a resource tab.
        selectResourceTab(tab, index) {
            this.selectedResourceTab = tab
            this.resetResourceTabEdit()
            this.resetResourceTabSelect()
            this.$set(tab, "isSelected", true)
            this.$nextTick(() => this.$refs.tabInputs[index].focus())
        },
        // Enable the label of a resource tab to be edited.
        editResourceTab(tab) {
            this.selectedResourceTab = tab
            this.resetResourceTabEdit()
            this.$set(tab, "isEditing", true)
        },
        // Reset the edit state of resource tabs.
        resetResourceTabEdit() {
            let selectedTab = this.caseObj.resource_tabs.find((t) => t.isEditing)
            if (selectedTab) {
                selectedTab.isEditing = false
            }
        },
        // Reset the selected state of resource tabs.
        resetResourceTabSelect() {
            let selectedTab = this.caseObj.resource_tabs.find((t) => t.isSelected)
            if (selectedTab) {
                selectedTab.isSelected = false
            }
        },
        // Move a resource tab left or right, skipping deleted tabs.
        shiftTabIndex(index, increment) {
            let newIndex = index + increment
            // Don't do anything if tab is already at the start or end of the array.
            if ((index == 0 && increment == -1) || newIndex >= this.caseObj.resource_tabs.length) {
                return
            }
            // Skip inactive tabs.
            let correctedIndex = null
            while (correctedIndex == null) {
                if (newIndex >= this.caseObj.resource_tabs.length) {
                    return
                }
                if (this.caseObj.resource_tabs[newIndex].isDeleted) {
                    newIndex = newIndex + increment
                } else {
                    correctedIndex = newIndex
                }
            }
            // Remove the tab from the array.
            let tab = this.caseObj.resource_tabs.splice(index, 1)[0]
            // Put it back at the new position.
            this.caseObj.resource_tabs.splice(correctedIndex, 0, tab)
        },
        // Open the remove resource tab modal.
        openRemoveTab(tab) {
            this.selectedResourceTab = tab
            $("#remove-tab-modal").modal("show")
        },
        // Mark a resource tab as deleted.
        removeResourceTab() {
            this.$set(this.selectedResourceTab, "isDeleted", true)
            $("#remove-tab-modal").modal("hide")
            this.selectedResourceTab = null
            this.resetResourceTabEdit()
            this.resetResourceTabSelect()
        },
    },
    created() {
        this.fetchData()
    },
    mounted() {
        let tabView = this.$route.query.view
        if (tabView && this.tabs.map((t) => t.value).includes(tabView)) {
            this.currentView = tabView
        }
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.font-500 {
    font-weight: 500;
}

.settings-nav-link {
    text-decoration: none;
    margin-top: 8px;
    @media (min-width: $breakpoint-sm) {
        margin-top: 0px;
    }
    margin-right: 24px;
}

.list-logo {
    width: 40px;
    height: 40px;
    background-color: white;
    border: 1px solid $gray-200 !important;
    padding: 2px;
    overflow: hidden;
}

.list-logo-img {
    object-fit: cover;
    width: 100%;
}

.alt-text-input {
    max-width: 275px;
}

$tab-tool-offset: 0px;

.selected-tab-tools {
    top: $tab-tool-offset;
    height: 0px;
}
</style>